import * as React from 'react';
import { Button, Checkbox, FormControlLabel, Typography } from '@material-ui/core';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons';
import { DateTime } from 'luxon';
import LuxonUtils from '@date-io/luxon';
import MuiPickersUtilsProvider from 'material-ui-pickers/MuiPickersUtilsProvider';
import { InlineDatePicker } from 'material-ui-pickers/DatePicker';
import { dateFormatted } from '../../datetime';

interface IProps {
  endDate: DateTime;
  showTba?: boolean;
  startDate: DateTime;
  tba: boolean;
  onChange: (startDate: DateTime, endDate: DateTime) => void;
  onTbaChange: (tba: boolean) => void;
}

export interface IMonitoringDateRange {
  endDate: DateTime;
  startDate: DateTime;
}

export type MonitoringRangePreset = 'week' | 'month';

const parseDate = (value: string | null): DateTime | null => {
  const date = DateTime.fromISO(value ?? '');
  return date.isValid ? date.startOf('day') : null;
};

export const isMonitoringStartDate = (
  date: DateTime,
  preset: MonitoringRangePreset,
): boolean => preset === 'week' ? date.weekday === 1 : date.day === 1;

export const createMonitoringDateRange = (
  startDate: DateTime,
  preset: MonitoringRangePreset,
): IMonitoringDateRange => {
  const normalizedStart = preset === 'week' ? startDate.startOf('week') : startDate.startOf('month');

  return {
    startDate: normalizedStart,
    endDate:
      preset === 'week'
        ? normalizedStart.plus({ days: 6 })
        : normalizedStart.plus({ months: 1 }).minus({ days: 1 }),
  };
};

export const inferMonitoringRangePreset = (
  startDate: DateTime,
  endDate: DateTime,
): MonitoringRangePreset => {
  const normalizedMonthEnd = createMonitoringDateRange(startDate, 'month').endDate;
  const rollingMonthEnd = startDate.plus({ months: 1 }).minus({ days: 1 });

  if (endDate.toISODate() === normalizedMonthEnd.toISODate() || endDate.toISODate() === rollingMonthEnd.toISODate()) {
    return 'month';
  }

  return 'week';
};

export const parseMonitoringDateRange = (search: string): IMonitoringDateRange => {
  const params = new URLSearchParams(search);
  const startDate = parseDate(params.get('startDate'));
  const endDate = parseDate(params.get('endDate'));

  if (startDate && endDate && endDate.toMillis() >= startDate.toMillis()) {
    return createMonitoringDateRange(startDate, inferMonitoringRangePreset(startDate, endDate));
  }

  const legacyDate = parseDate(params.get('date'));
  if (legacyDate) {
    return createMonitoringDateRange(legacyDate, 'week');
  }

  return createMonitoringDateRange(DateTime.local().startOf('day'), 'week');
};

export const parseMonitoringTba = (search: string): boolean => new URLSearchParams(search).get('tba') === 'true';

export const MonitoringDateNavigator: React.FC<IProps> = ({
  endDate,
  showTba,
  startDate,
  tba,
  onChange,
  onTbaChange,
}) => {
  const [rangePreset, setRangePreset] = React.useState<MonitoringRangePreset>(
    inferMonitoringRangePreset(startDate, endDate),
  );

  const setStartDate = (date: DateTime) => {
    const range = createMonitoringDateRange(date, rangePreset);
    onChange(range.startDate, range.endDate);
  };
  const setPreset = (preset: MonitoringRangePreset) => {
    const range = createMonitoringDateRange(startDate, preset);
    setRangePreset(preset);
    onChange(range.startDate, range.endDate);
  };

  return (
    <div className="monitoring-date">
      <Typography variant="h6">
        {startDate.toFormat('dd/MM/yyyy')} - {endDate.toFormat('dd/MM/yyyy')}
      </Typography>
      <div className="monitoring-date-navigator">
        <MuiPickersUtilsProvider utils={LuxonUtils}>
          <InlineDatePicker
            onlyCalendar
            label="Delivery date from"
            value={startDate}
            onChange={setStartDate}
            shouldDisableDate={(date: DateTime) => !isMonitoringStartDate(date, rangePreset)}
            labelFunc={(date: DateTime) => dateFormatted(date)}
            InputProps={{
              disableUnderline: true,
              inputProps: { 'aria-label': 'Delivery date from' },
            }}
            leftArrowIcon={<KeyboardArrowLeft />}
            rightArrowIcon={<KeyboardArrowRight />}
            disabled={tba}
          />
        </MuiPickersUtilsProvider>
        <Button
          color={rangePreset === 'week' ? 'primary' : 'default'}
          disabled={tba}
          onClick={() => setPreset('week')}
          variant={rangePreset === 'week' ? 'contained' : 'text'}
        >
          Week
        </Button>
        <Button
          color={rangePreset === 'month' ? 'primary' : 'default'}
          disabled={tba}
          onClick={() => setPreset('month')}
          variant={rangePreset === 'month' ? 'contained' : 'text'}
        >
          Month
        </Button>
        {showTba && (
          <FormControlLabel
            control={<Checkbox checked={tba} onChange={(event) => onTbaChange(event.target.checked)} />}
            label="TBA"
          />
        )}
      </div>
      {showTba && tba && (
        <Typography variant="caption" className="monitoring-tba-note">
          Showing TBA orders created or updated in the last 7 days.
        </Typography>
      )}
    </div>
  );
};
